import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { debounceTime, startWith } from 'rxjs/operators';
import { Project, isSelectableProject } from 'src/app/classes/project.class';
import { User } from 'src/app/classes/user.class';
import { CodaltComponent } from 'src/app/codalt.component';
import { AuthorisationService } from 'src/app/services/auth/authorisation.service';
import { ProjectService } from 'src/app/services/project.service';
import { UserService } from 'src/app/services/user/user.service';
import { LocalStorage } from 'src/app/storage.class';
import { DAY_IN_MILLISECONDS } from 'src/app/utils.class';
import { ProjectEditDialogComponent } from './project-edit-dialog/project-edit-dialog.component';

@Component({
    selector: 'app-projects',
    templateUrl: './projects.component.html',
    styleUrls: ['./projects.component.scss'],
    standalone: false,
})
export class ProjectsComponent extends CodaltComponent implements OnInit {
    fcProjectSearch = new FormControl('');
    fcUserFilter = new FormControl(null);
    fcProjectManagerFilter = new FormControl(null);

    projects: Project[] = [];
    users: User[] = [];
    usersMap: Map<number, User> = new Map();
    filteredProjects: Project[] = [];
    usersLoaded = false;
    private isInitialLoad = true;
    projectmanagerMap: User[];
    executorMap: User[];
    private readonly FILTER_STORAGE_KEY = 'projectFilters';

    constructor(
        private projectService: ProjectService,
        private userService: UserService,
        private router: Router,
        private dialog: MatDialog,
        private activatedRoute: ActivatedRoute
    ) {
        super();
    }

    ngOnInit(): void {
        this.subscriptions.add(
            this.activatedRoute.params.subscribe(params => {
                if (params['project']) {
                    this.openProjectEditDialog(params['project']);
                }
            })
        );

        // Load users first
        const users$ = this.userService.getList();

        this.subscriptions.add(
            users$.subscribe({
                next: userResponse => {
                    this.users = userResponse;

                    this.usersMap = new Map(userResponse.map(user => [user.id, user]));
                    this.usersLoaded = true;

                    this.projectmanagerMap = this.users.filter(user =>
                        AuthorisationService.hasRole(this.environment.roles.Projectmanager, user)
                    );
                    this.executorMap = this.users.filter(
                        user =>
                            AuthorisationService.hasRole(this.environment.roles.Executor, user) ||
                            AuthorisationService.hasRole(this.environment.roles.Asphaltexecutor, user)
                    );

                    this.setDefaultFilters();
                    this.setupFilters();
                },
                error: error => {
                    console.error('Error fetching users', error);
                },
            })
        );
    }

    private setDefaultFilters(): void {
        // First try to load saved filters
        const savedFilters = this.loadSavedFilters();

        if (savedFilters) {
            // Apply saved filters
            this.fcProjectSearch.setValue(savedFilters.searchTerm || '');
            this.fcUserFilter.setValue(savedFilters.userId);
            this.fcProjectManagerFilter.setValue(savedFilters.projectManagerId);
            this.isInitialLoad = false;
        } else if (this.isInitialLoad && !this.fcProjectManagerFilter.value && !this.fcUserFilter.value) {
            // Only set defaults if no saved filters and it's the initial load
            this.setDefaultUserFilters();
            this.isInitialLoad = false;
        }

        this.loadProjects();
    }

    private loadProjects(): void {
        if (!this.usersLoaded) return;

        const searchParams = this.buildSearchParams();

        this.subscriptions.add(
            this.projectService
                .searchProjects(searchParams.searchTerm, {
                    user_id: searchParams.user_id,
                    project_manager_id: searchParams.project_manager_id,
                })
                .subscribe(projects => {
                    this.projects = projects?.filter(isSelectableProject) || [];
                    this.mapUserNamesToProjects();
                    this.filteredProjects = this.projects;
                })
        );
    }

    private setDefaultUserFilters(): void {
        const currentUser = LocalStorage.getUser();
        if (!currentUser) return;

        if (AuthorisationService.hasRole(this.environment.roles.Projectmanager, currentUser)) {
            this.fcProjectManagerFilter.setValue(currentUser.id);
        }

        if (
            AuthorisationService.hasRole(this.environment.roles.Executor, currentUser) ||
            AuthorisationService.hasRole(this.environment.roles.Asphaltexecutor, currentUser)
        ) {
            this.fcUserFilter.setValue(currentUser.id);
        }
    }

    private setupFilters(): void {
        this.subscriptions.add(
            combineLatest([
                this.fcProjectSearch.valueChanges.pipe(debounceTime(200), startWith('')),
                this.fcUserFilter.valueChanges.pipe(startWith(null)),
                this.fcProjectManagerFilter.valueChanges.pipe(startWith(null)),
            ]).subscribe(() => {
                // Save current filters to localStorage
                this.saveCurrentFilters();
                this.loadProjects();
            })
        );
    }

    private buildSearchString(): string {
        const searchTerms: string[] = [];

        // Add text search term
        const searchTerm = this.fcProjectSearch.value;
        if (searchTerm && searchTerm.trim()) {
            searchTerms.push(searchTerm.trim());
        }

        return searchTerms.join(' ');
    }

    private buildSearchParams() {
        return {
            searchTerm: this.buildSearchString(),
            user_id: this.fcUserFilter.value,
            project_manager_id: this.fcProjectManagerFilter.value,
        };
    }

    private mapUserNamesToProjects(): void {
        if (!this.projects || this.usersMap.size === 0) return;

        this.projects.forEach(project => {
            if (project.projectmanager_id) {
                project.projectmanager = this.usersMap.get(project.projectmanager_id);
            }
            if (project.performer_id) {
                project.performer = this.usersMap.get(project.performer_id);
            }
            if (project.asphalt_performer_id) {
                project.asphalt_performer = this.usersMap.get(project.asphalt_performer_id);
            }
        });
    }

    private saveCurrentFilters(): void {
        const filters = {
            searchTerm: this.fcProjectSearch.value || '',
            userId: this.fcUserFilter.value,
            projectManagerId: this.fcProjectManagerFilter.value,
            timestamp: Date.now(),
        };

        try {
            sessionStorage.setItem(this.FILTER_STORAGE_KEY, JSON.stringify(filters));
        } catch (error) {
            console.warn('Could not save filters to sessionStorage:', error);
        }
    }

    private loadSavedFilters(): any {
        try {
            const saved = sessionStorage.getItem(this.FILTER_STORAGE_KEY);
            if (saved) {
                const filters = JSON.parse(saved);
                // Check if filters are not too old (optional: expire after 24 hours)
                const maxAge = DAY_IN_MILLISECONDS; // 24 hours in milliseconds
                if (filters.timestamp && Date.now() - filters.timestamp < maxAge) {
                    return filters;
                }
            }
        } catch (error) {
            console.warn('Could not load filters from sessionStorage:', error);
        }
        return null;
    }

    openProjectEditDialog(projectId?: string, parent_id?: string): void {
        const dialogRef = this.dialog.open(ProjectEditDialogComponent, {
            width: '800px',
            maxWidth: '90vw',
            disableClose: true,
            data: { projectId: projectId, parent_id: parent_id ?? this.fcProjectSearch.value },
        });

        this.subscriptions.add(
            dialogRef.afterClosed().subscribe(result => {
                if (result && result.saved) {
                    // Project was saved, refresh the list
                    this.loadProjects();

                    // Update the specific project in the current list to avoid full reload if needed
                    if (result.updatedProject) {
                        const index = this.projects.findIndex(
                            p => p.afas_project_id === result.updatedProject.afas_project_id
                        );
                        if (index !== -1) {
                            this.projects[index] = result.updatedProject;
                            this.mapUserNamesToProjects();
                            this.filteredProjects = this.projects;
                        }
                    }
                }
            })
        );
    }

    goToDayReport(project: Project): void {
        if (!project || !project.afas_project_id) return;
        this.router.navigate(['/project-dagrapport', project.afas_project_id]);
    }

    goToWeekReport(project: Project): void {
        if (!project || !project.afas_project_id) return;
        this.router.navigate(['/projects-overview'], { queryParams: { projectId: project.afas_project_id } });
    }

    goToSettlements(project: Project): void {
        if (!project || !project.afas_project_id) return;
        const currentYear = new Date().getFullYear();
        const currentWeeknumber = this.getCurrentWeekNumber();
        this.router.navigate([`/settlements/${currentWeeknumber}/${currentYear}/entities/${project.afas_project_id}`]);
    }

    private getCurrentWeekNumber(): number {
        const currentDate = new Date();
        const startOfYear = new Date(currentDate.getFullYear(), 0, 1);
        const pastDaysOfYear = (currentDate.valueOf() - startOfYear.valueOf()) / 86400000;
        return Math.ceil((pastDaysOfYear + startOfYear.getDay() + 1) / 7);
    }
}
